if (!customElements.get('dl-email-gift-y')) {
  customElements.define('dl-email-gift-y', class extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.form = this.querySelector('[data-subscribe-form]');
      this.input = this.querySelector('[data-email-input]');
      this.button = this.querySelector('[data-submit-button]');
      this.label = this.querySelector('[data-submit-label]');
      this.message = this.querySelector('[data-message]');
      this.giftButton = this.querySelector('.dl-email-gift-y__gift-button');
      this.defaultLabel = this.label?.textContent || '';
      this.onSubmit = this.submit.bind(this);
      this.onInput = this.clearMessage.bind(this);
      this.onGiftClick = this.handleGiftClick.bind(this);
      this.form?.addEventListener('submit', this.onSubmit);
      this.input?.addEventListener('input', this.onInput);
      this.giftButton?.addEventListener('click', this.onGiftClick);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
      this.giftButton?.removeEventListener('click', this.onGiftClick);
      this._bound = false;
    }

    clearMessage() {
      this.input?.removeAttribute('aria-invalid');
      this.message?.classList.remove('is-visible', 'is-error');
      if (this.message) this.message.textContent = '';
    }

    showMessage(text, isError = false) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('is-error', isError);
      this.message.classList.add('is-visible');
    }

    setLoading(loading) {
      if (this.button) this.button.disabled = loading;
      if (this.form) this.form.setAttribute('aria-busy', String(loading));
      if (this.label) this.label.textContent = loading ? (this.dataset.loadingLabel || 'Submitting...') : this.defaultLabel;
    }

    getCustomAttributes() {
      const attributes = JSON.parse(this.dataset.customAttributes || '{}');
      if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) {
        throw new Error('customAttributes must be a JSON object');
      }
      return attributes;
    }

    trackSubscribeSubmit(email, campaignId) {
      this.captureAmplitude('subscribe_submit', {
        email,
        source: 'dl-email-gift-y',
        campaign_id: campaignId
      });
    }

    handleGiftClick(event) {
      this.trackGiftClick(event);
      const button = event.currentTarget;
      if (button.dataset.giftButtonType !== 'anchor') return;

      event.preventDefault();
      const href = button.getAttribute('href');
      if (!href || href === '#') return;
      document.getElementById(href.slice(1))?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    trackGiftClick(event) {
      const button = event.currentTarget;
      this.captureAmplitude('btn_click', {
        btn_text: (button.textContent || '').trim(),
        url: button.href,
        page_url: window.location.href
      });
    }

    captureAmplitude(eventName, eventData) {
      try {
        // Only use Amplitude; the subscription API handles Braze on the server.
        const amplitude = window.DLPixelH?.getAllInstances?.().find((instance) => {
          const name = instance?.name || instance?.constructor?.name;
          return name === 'AmplitudePixel' || name === 'DLAmplitudePixel';
        });
        if (typeof amplitude?.capture === 'function') {
          Promise.resolve(amplitude.capture(eventName, eventData)).catch(() => {});
        } else if (typeof window.Shopify?.analytics?.publish === 'function') {
          Promise.resolve(window.Shopify.analytics.publish(`[Petlibro] ${eventName}`, eventData)).catch(() => {});
        }
      } catch (error) {
        console.warn('[dl-email-gift-y] Event tracking failed:', error);
      }
    }

    getShopifyStore() {
      let braze_lastest_shopify_store = localStorage.getItem('braze_lastest_shopify_store');
      if (!braze_lastest_shopify_store) {
        let subdomain = window.location.hostname.split('.')[0];
        subdomain = subdomain === 'petlibro' || subdomain === 'ca' ? 'us' : subdomain;
        localStorage.setItem('braze_lastest_shopify_store', subdomain);
        braze_lastest_shopify_store = subdomain;
      }
      return braze_lastest_shopify_store;
    }

    waitForBraze() {
      if (typeof DLPixelH === 'undefined') {
        return Promise.resolve();
      }
      if (typeof DLPixelH.whenBrazeReady === 'function') {
        return new Promise((resolve) => {
          DLPixelH.whenBrazeReady(() => resolve()).catch(() => resolve());
        });
      }
      return Promise.resolve();
    }

    submitPopupBraze(email) {
      const popId = (this.dataset.popId || '').trim();
      const fieldKey = `pop-${popId}-email`;
      const finishedTimeKey = `pop-${popId}-finished-time`;
      const popFinishedTime = new Date().toISOString();

      const blockData = {
        [`pop_prop_${fieldKey}`]: email,
        [finishedTimeKey]: popFinishedTime,
        pop_id: popId,
        currentURLInfo: window.DLPixelDataHelper?.URLHelper?.getCurrentPageInfo?.() ?? null,
        pop_type: 'form',
        shopify_store: this.getShopifyStore()
      };

      try {
        if (typeof DLPixelH === 'undefined') {
          throw new Error('DLPixelH is not available');
        }

        DLPixelH.addToUserArrayProperty('pop-id', `pop-${popId}`);
        DLPixelH.identify(email);
        DLPixelH.addToUserArrayProperty(fieldKey, email);
        DLPixelH.setUserProperty(finishedTimeKey, popFinishedTime);
        DLPixelH.capture('pop_up_submit', blockData);

        if (typeof braze !== 'undefined' && typeof braze?.requestImmediateDataFlush === 'function') {
          braze.requestImmediateDataFlush();
        }
      } catch (error) {
        console.warn('[dl-email-gift-y] Braze integration error, falling back to basic capture:', error);
        if (typeof DLPixelH?.capture === 'function') {
          DLPixelH.capture('pop_up_submit', blockData);
        } else {
          this.captureAmplitude('pop_up_submit', blockData);
        }
      }
    }

    async submit(event) {
      event.preventDefault();
      if (this.button?.disabled) return;
      this.clearMessage();

      const email = this.input?.value.trim() || '';
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!this.input?.checkValidity() || !email || !emailRegex.test(email)) {
        this.input?.setAttribute('aria-invalid', 'true');
        this.showMessage(this.dataset.emailErrorMessage || 'Please enter a valid email address.', true);
        this.input?.focus();
        return;
      }

      this.setLoading(true);
      try {
        const subscribeMode = (this.dataset.subscribeMode || 'api').trim();

        if (subscribeMode === 'popup_braze') {
          await this.waitForBraze();
          this.submitPopupBraze(email);
        } else {
          const campaignId = (this.dataset.campaignId || '').trim();
          if (!campaignId) throw new Error('Campaign ID is required');
          const customAttributes = this.getCustomAttributes();
          this.trackSubscribeSubmit(email, campaignId);

          const response = await fetch(`/apps/petlibro-api/email-subscription/${encodeURIComponent(campaignId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              tag: this.dataset.tag || 'newsletter',
              customAttributes
            })
          });

          const result = await response.json().catch(() => null);
          if (!response.ok || result?.success === false) {
            throw new Error(result?.message || `Subscription failed (${response.status})`);
          }
        }

        this.form?.reset();
        this.showMessage(this.dataset.successMessage || 'Thanks for subscribing!');
      } catch (error) {
        console.error('[dl-email-gift-y] Subscription failed:', error);
        this.showMessage(this.dataset.errorMessage || 'Something went wrong. Please try again.', true);
      } finally {
        this.setLoading(false);
      }
    }
  });
}
